import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressBar } from "primereact/progressbar";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { getChatbotAnalytics, setChatbotBudget } from "../../services/chatbot";
// Separate `import type` rather than an inline `type` specifier: CRA 5's babel-preset-react-app
// predates support for inline type specifiers and fails to parse them, which broke the build.
import type { ChatbotAnalytics } from "../../services/chatbot";

const toYearMonth = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const StatCard = ({
  title,
  value,
  icon,
  color,
  subtitle,
}: {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  subtitle?: string;
}) => (
  <Card className="h-full" style={{ borderTop: `3px solid ${color}` }}>
    <div className="flex justify-content-between align-items-start">
      <div>
        <p
          className="text-xs font-semibold uppercase m-0 mb-2 text-color-secondary"
          style={{ letterSpacing: "0.06em" }}
        >
          {title}
        </p>
        <p className="text-3xl font-bold m-0 text-color">{value}</p>
        {subtitle && (
          <p className="text-xs text-color-secondary mt-1 mb-0">{subtitle}</p>
        )}
      </div>
      <div
        className="flex align-items-center justify-content-center border-round-lg"
        style={{ width: 42, height: 42, background: `${color}18` }}
      >
        <i className={`${icon} text-lg`} style={{ color }} />
      </div>
    </div>
  </Card>
);

const AiBudgetPage = () => {
  const toast = useRef<Toast>(null);

  const [analyticsMonth, setAnalyticsMonth] = useState<Date | null>(null);
  const [analytics, setAnalytics] = useState<ChatbotAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  const [budgetEur, setBudgetEur] = useState<number | null>(null);
  const [budgetMonth, setBudgetMonth] = useState<Date | null>(null);
  const [budgetSaving, setBudgetSaving] = useState(false);

  const fetchAnalytics = useCallback(async (month?: string) => {
    setAnalyticsLoading(true);
    try {
      const data = await getChatbotAnalytics(month);
      setAnalytics(data);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load chatbot analytics.",
        life: 3000,
      });
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthChange = (date: Date | null) => {
    setAnalyticsMonth(date);
    fetchAnalytics(date ? toYearMonth(date) : undefined);
  };

  const executeSaveBudget = async () => {
    setBudgetSaving(true);
    try {
      await setChatbotBudget(
        budgetEur!,
        budgetMonth ? toYearMonth(budgetMonth) : undefined
      );
      toast.current?.show({
        severity: "success",
        summary: "Budget saved",
        detail: "Budget updated successfully.",
        life: 4000,
      });
      fetchAnalytics(analyticsMonth ? toYearMonth(analyticsMonth) : undefined);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save budget.";
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail,
        life: 6000,
      });
    } finally {
      setBudgetSaving(false);
    }
  };

  const handleSaveBudget = () => {
    if (!budgetEur || budgetEur <= 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Budget must be greater than 0.",
        life: 3000,
      });
      return;
    }
    const monthLabel = budgetMonth ? toYearMonth(budgetMonth) : "the current month";
    confirmDialog({
      header: "Confirm Budget Update",
      message: `Set a budget of €${budgetEur.toFixed(2)} for ${monthLabel}? This will override the session cap.`,
      icon: "pi pi-euro",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      accept: executeSaveBudget,
    });
  };

  const sessionCap = analytics?.sessionCap ?? 0;
  const sessionsUsed = analytics?.totalSessionsThisMonth ?? 0;
  const sessionsRemaining = analytics?.sessionsRemaining ?? 0;
  const usagePercent =
    sessionCap > 0 ? Math.min(Math.round((sessionsUsed / sessionCap) * 100), 100) : 0;

  const usageColor =
    usagePercent >= 90 ? "#ef4444" : usagePercent >= 70 ? "#f59e0b" : "#22c55e";

  const topUsers = analytics?.topUsers ?? [];

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* ── Page header ─────────────────────────────────────────── */}
      <div
        className="flex align-items-center justify-content-between flex-wrap gap-3 p-4 mb-4 border-round-xl"
        style={{
          background: "linear-gradient(135deg, var(--primary-50) 0%, var(--surface-card) 100%)",
          border: "1px solid var(--surface-border)",
        }}
      >
        <div className="flex align-items-center gap-3">
          <div
            className="flex align-items-center justify-content-center border-round-lg"
            style={{ width: 48, height: 48, background: "var(--primary-color)", flexShrink: 0 }}
          >
            <i className="pi pi-microchip-ai text-xl text-white" />
          </div>
          <div>
            <h2 className="m-0 text-2xl font-bold text-color">AI Chatbot Dashboard</h2>
            <p className="m-0 mt-1 text-sm text-color-secondary">
              Monitor session usage and manage monthly AI spend
            </p>
          </div>
        </div>
        <div className="flex align-items-center gap-2">
          <Calendar
            value={analyticsMonth}
            onChange={(e) => handleMonthChange(e.value as Date | null)}
            view="month"
            dateFormat="MM yy"
            placeholder="Current month"
            showIcon
            style={{ width: "13rem" }}
            readOnlyInput
          />
          {analyticsMonth && (
            <Button
              icon="pi pi-times"
              rounded
              text
              severity="secondary"
              tooltip="Reset to current month"
              tooltipOptions={{ position: "top" }}
              onClick={() => handleMonthChange(null)}
            />
          )}
        </div>
      </div>

      {/* ── Analytics ───────────────────────────────────────────── */}
      {analyticsLoading ? (
        <div className="flex justify-content-center align-items-center" style={{ minHeight: "220px" }}>
          <ProgressSpinner />
        </div>
      ) : analytics ? (
        <>
          {/* Stat cards */}
          <div className="grid mb-3">
            <div className="col-12 md:col-4">
              <StatCard
                title="Unique Users"
                value={analytics.uniqueUsersThisMonth ?? 0}
                icon="pi pi-users"
                color="#3b82f6"
                subtitle="chatbot users this month"
              />
            </div>
            <div className="col-12 md:col-4">
              <StatCard
                title="Total Sessions"
                value={sessionsUsed}
                icon="pi pi-comments"
                color="#8b5cf6"
                subtitle={`of ${sessionCap} session cap`}
              />
            </div>
            <div className="col-12 md:col-4">
              <StatCard
                title="Users at Daily Limit"
                value={analytics.usersAtDailyLimit ?? 0}
                icon="pi pi-ban"
                color={analytics.usersAtDailyLimit > 0 ? "#f59e0b" : "#22c55e"}
                subtitle="blocked from sessions today"
              />
            </div>
          </div>

          {/* Usage progress card */}
          <Card className="mb-3">
            <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-xs font-semibold uppercase text-color-secondary m-0 mb-1" style={{ letterSpacing: "0.06em" }}>
                  Session Usage — {analytics.month}
                </p>
                <div className="flex align-items-center gap-3">
                  <span className="text-2xl font-bold text-color">{sessionsUsed}</span>
                  <span className="text-color-secondary">/</span>
                  <span className="text-lg text-color-secondary">{sessionCap} sessions</span>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="text-right">
                  <p className="text-xs text-color-secondary m-0">Remaining</p>
                  <p className="text-xl font-bold m-0" style={{ color: usageColor }}>
                    {sessionsRemaining}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-color-secondary m-0">Used</p>
                  <p className="text-xl font-bold m-0">{usagePercent}%</p>
                </div>
              </div>
            </div>
            <ProgressBar
              value={usagePercent}
              style={{ height: "10px" }}
              color={usageColor}
              showValue={false}
            />
            {usagePercent >= 90 && (
              <p className="text-xs mt-2 mb-0" style={{ color: "#ef4444" }}>
                <i className="pi pi-exclamation-triangle mr-1" />
                Session cap almost reached. Consider increasing the budget for this month.
              </p>
            )}
          </Card>

          {/* Bottom row: top users + budget form */}
          <div className="grid">
            {topUsers.length > 0 && (
              <div className="col-12 lg:col-5">
                <Card title="Top Users This Month" style={{ height: "100%" }}>
                  <DataTable
                    value={topUsers}
                    stripedRows
                    size="small"
                    showGridlines={false}
                  >
                    <Column
                      header="#"
                      body={(_row, opts) => (
                        <span className="text-color-secondary text-xs">{opts.rowIndex + 1}</span>
                      )}
                      style={{ width: "3rem" }}
                    />
                    <Column
                      field="userEmaid"
                      header="EMAID"
                      body={(row) => (
                        <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                          {row.userEmaid}
                        </span>
                      )}
                    />
                    <Column
                      field="sessionsThisMonth"
                      header="Sessions"
                      body={(row) => (
                        <Tag
                          value={row.sessionsThisMonth}
                          style={{ background: "var(--primary-color)", minWidth: "3rem" }}
                        />
                      )}
                      style={{ width: "7rem", textAlign: "center" }}
                    />
                  </DataTable>
                </Card>
              </div>
            )}

            <div className={topUsers.length > 0 ? "col-12 lg:col-7" : "col-12"}>
              <Card title="Set Monthly Budget" style={{ height: "100%" }}>
                <p className="mt-0 mb-4 text-color-secondary text-sm">
                  Specify a budget in euros to automatically compute and override the session
                  cap. Uses current ElevenLabs pricing.
                </p>
                <div className="grid">
                  <div className="col-12 md:col-6">
                    <label className="block mb-2 text-sm font-medium text-color">
                      Budget (EUR) <span className="text-red-500">*</span>
                    </label>
                    <InputNumber
                      value={budgetEur}
                      onValueChange={(e) => setBudgetEur(e.value ?? null)}
                      mode="currency"
                      currency="EUR"
                      locale="de-DE"
                      min={0.01}
                      placeholder="e.g. 200.00"
                      className="w-full"
                    />
                  </div>
                  <div className="col-12 md:col-6">
                    <label className="block mb-2 text-sm font-medium text-color">
                      Month
                      <span className="text-color-secondary font-normal ml-1 text-xs">(optional)</span>
                    </label>
                    <Calendar
                      value={budgetMonth}
                      onChange={(e) => setBudgetMonth(e.value as Date | null)}
                      view="month"
                      dateFormat="MM yy"
                      placeholder="Current month"
                      showIcon
                      className="w-full"
                      readOnlyInput
                    />
                  </div>
                </div>

                <div className="flex justify-content-end mt-4">
                  <Button
                    label="Save Budget"
                    icon="pi pi-save"
                    loading={budgetSaving}
                    onClick={handleSaveBudget}
                  />
                </div>

              </Card>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AiBudgetPage;
